import { useEffect, useState } from 'react'

/**
 * Every shape of link a candidate CV arrives as, and how to show it.
 *
 * Measured across the role mappings on UAT: 2,804 PDFs, 478 Google Drive
 * links, 210 .doc/.docx and 326 with no extension at all (object-storage keys —
 * mostly PDFs, some JPEGs a candidate photographed their resume into). Every
 * one of them has to render; "we can't show this" is not an answer a recruiter
 * can act on.
 */
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif']
const DOCUMENT_EXTENSIONS = ['doc', 'docx', 'rtf', 'odt', 'ppt', 'pptx', 'xls', 'xlsx', 'txt']

const getExtension = url => url?.split('?')[0]?.split('#')[0]?.split('.')?.pop()?.toLowerCase() || ''

// The three ways a Drive/Docs id shows up in the links we hold. Tally intake
// writes `open?id=`; a pasted share link is `/file/d/<id>/view`; a Google Doc
// is `/document/d/<id>/edit`.
const GOOGLE_ID_PATTERNS = [/\/(?:file|document|presentation|spreadsheets)\/d\/([\w-]{10,})/, /[?&]id=([\w-]{10,})/]

export const isGoogleLink = url =>
  Boolean(url?.includes('drive.google.com') || url?.includes('docs.google.com'))

/**
 * Google's own frameable preview for a Drive link.
 *
 * A Drive URL is an HTML page behind Google's auth, not a file — fetching one
 * yields markup, and CORS blocks it anyway. `/preview` serves no
 * X-Frame-Options, so embedding it is the only way to show these 478 CVs. A
 * file the recruiter cannot open renders Google's own "request access" page,
 * which is the truthful thing to show.
 *
 * @returns {string|null} the embed URL, or null when the link is not Google's
 */
export const toGoogleEmbedUrl = url => {
  if (!isGoogleLink(url)) return null
  const id = GOOGLE_ID_PATTERNS.reduce((found, pattern) => found || url.match(pattern)?.[1], null)
  if (!id) return null
  const docType = url.match(/\/(document|presentation|spreadsheets)\/d\//)?.[1]
  return docType
    ? `https://docs.google.com/${docType}/d/${id}/preview`
    : `https://drive.google.com/file/d/${id}/preview`
}

/**
 * Google's document viewer, used for the formats a browser cannot draw itself.
 *
 * Office Web Viewer was tried first and rendered nothing: a good third of our
 * `.doc` uploads are actually docx zips saved under the wrong extension, and it
 * trusts the extension. gview sniffs the file, so it renders those correctly.
 * It fetches server-side, so the URL must be publicly reachable — which the
 * CloudFront and OCI buckets we store CVs in are.
 */
export const toDocumentViewerUrl = url =>
  `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(url)}`

const fromExtension = url => {
  const extension = getExtension(url)
  if (extension === 'pdf') return 'pdf'
  if (IMAGE_EXTENSIONS.includes(extension)) return 'image'
  if (DOCUMENT_EXTENSIONS.includes(extension)) return 'document'
  return null
}

const fromContentType = contentType => {
  if (contentType?.startsWith('image/')) return 'image'
  if (contentType?.includes('pdf')) return 'pdf'
  return contentType ? 'document' : null
}

/**
 * What kind of document a CV link points at.
 *
 * The extension answers it for most links. The 326 object-storage keys that
 * carry no extension are settled by a HEAD request — both buckets send
 * `Access-Control-Allow-Origin: *`, so the Content-Type is readable. If that
 * request fails we guess `pdf`, the overwhelming majority, and a failed render
 * falls through to the document viewer anyway.
 *
 * @param {string|null} url
 * @returns {{kind: 'pdf'|'image'|'document'|'google'|null, isSniffing: boolean}}
 */
export const useUploadKind = url => {
  const [sniffed, setSniffed] = useState(null)
  const [isSniffing, setIsSniffing] = useState(false)

  const known = url ? fromExtension(url) : null
  const needsSniff = Boolean(url) && !known && !isGoogleLink(url)

  useEffect(() => {
    if (!needsSniff) {
      setSniffed(null)
      return undefined
    }

    let cancelled = false
    setIsSniffing(true)

    const sniff = async () => {
      try {
        const response = await fetch(url, { method: 'HEAD' })
        if (!cancelled) setSniffed(fromContentType(response.headers.get('content-type')) || 'pdf')
      } catch (error) {
        // Opaque response, offline, a bucket without CORS — assume the common
        // case rather than refusing to show anything.
        if (!cancelled) setSniffed('pdf')
      } finally {
        if (!cancelled) setIsSniffing(false)
      }
    }

    sniff()

    return () => {
      cancelled = true
    }
  }, [url, needsSniff])

  if (!url) return { kind: null, isSniffing: false }
  if (isGoogleLink(url)) return { kind: 'google', isSniffing: false }
  return { kind: known || sniffed, isSniffing }
}
